export type PasskeyCredentialJSON = {
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    attestationObject?: string
    authenticatorData?: string
    signature?: string
    userHandle?: string | null
  }
}

type JSONMethodCredential = typeof PublicKeyCredential & {
  parseCreationOptionsFromJSON?: (
    options: Record<string, unknown>,
  ) => PublicKeyCredentialCreationOptions
  parseRequestOptionsFromJSON?: (
    options: Record<string, unknown>,
  ) => PublicKeyCredentialRequestOptions
}

const publicKeyCredential = () =>
  typeof window !== 'undefined' &&
  typeof window.PublicKeyCredential !== 'undefined'
    ? (window.PublicKeyCredential as JSONMethodCredential)
    : null

export function isPasskeySupported() {
  return Boolean(
    publicKeyCredential() &&
      typeof navigator !== 'undefined' &&
      navigator.credentials &&
      typeof navigator.credentials.create === 'function' &&
      typeof navigator.credentials.get === 'function',
  )
}

function base64urlToBytes(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  const binary = window.atob(padded)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}

function bytesToBase64url(value: ArrayBuffer | ArrayBufferView) {
  const bytes = value instanceof ArrayBuffer
    ? new Uint8Array(value)
    : new Uint8Array(value.buffer, value.byteOffset, value.byteLength)
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function decodeCreationOptions(options: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const user = options.user as Record<string, unknown> | undefined
  const excludeCredentials = Array.isArray(options.excludeCredentials)
    ? options.excludeCredentials.map((descriptor) => {
        const item = descriptor as Record<string, unknown>
        return { ...item, id: base64urlToBytes(String(item.id)) } as unknown as PublicKeyCredentialDescriptor
      })
    : undefined
  return {
    ...options,
    challenge: base64urlToBytes(String(options.challenge)),
    user: {
      ...user,
      id: base64urlToBytes(String(user?.id)),
    } as unknown as PublicKeyCredentialUserEntity,
    excludeCredentials,
  } as unknown as PublicKeyCredentialCreationOptions
}

function decodeRequestOptions(options: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  const allowCredentials = Array.isArray(options.allowCredentials)
    ? options.allowCredentials.map((descriptor) => {
        const item = descriptor as Record<string, unknown>
        return { ...item, id: base64urlToBytes(String(item.id)) } as unknown as PublicKeyCredentialDescriptor
      })
    : undefined
  return {
    ...options,
    challenge: base64urlToBytes(String(options.challenge)),
    allowCredentials,
  } as PublicKeyCredentialRequestOptions
}

export function parseCreationOptions(options: Record<string, unknown>) {
  const parser = publicKeyCredential()?.parseCreationOptionsFromJSON
  return parser ? parser(options) : decodeCreationOptions(options)
}

export function parseRequestOptions(options: Record<string, unknown>) {
  const parser = publicKeyCredential()?.parseRequestOptionsFromJSON
  return parser ? parser(options) : decodeRequestOptions(options)
}

export async function createPasskey(options: Record<string, unknown>) {
  if (!isPasskeySupported()) {
    throw new Error('Passkeys are not supported by this browser.')
  }
  const credential = await navigator.credentials.create({
    publicKey: parseCreationOptions(options),
  })
  return serializePasskeyCredential(credential)
}

export async function getPasskey(options: Record<string, unknown>) {
  if (!isPasskeySupported()) {
    throw new Error('Passkeys are not supported by this browser.')
  }
  const credential = await navigator.credentials.get({
    publicKey: parseRequestOptions(options),
  })
  return serializePasskeyCredential(credential)
}

export function serializePasskeyCredential(credential: Credential | null): PasskeyCredentialJSON {
  if (!(credential instanceof PublicKeyCredential)) {
    throw new Error('Your browser did not return a passkey credential.')
  }

  const response = credential.response
  const serialized: PasskeyCredentialJSON = {
    id: credential.id,
    rawId: bytesToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bytesToBase64url(response.clientDataJSON),
    },
  }
  if (response instanceof AuthenticatorAttestationResponse) {
    serialized.response.attestationObject = bytesToBase64url(response.attestationObject)
  } else if (response instanceof AuthenticatorAssertionResponse) {
    serialized.response.authenticatorData = bytesToBase64url(response.authenticatorData)
    serialized.response.signature = bytesToBase64url(response.signature)
    serialized.response.userHandle = response.userHandle
      ? bytesToBase64url(response.userHandle)
      : null
  }
  return serialized
}
